import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useSnackbar } from 'notistack'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { fmtDate, num } from '../ptl-merchant-setup/constants'
import { Banner } from '../ptl-merchant-setup/components/SetupUi'
import PtlPage from '../ptl-merchant-setup/shell/PtlPage'
import { errorText, fetchOdaBands, saveOdaBands } from './ptlOdaApi'
import { EDD_TIMING, MAX_EXTRA_DAYS, ODA_EYEBROW, ODA_TYPE_KEYS, odaTabs, typeLabel } from './constants'

const KEY = /^[A-Za-z0-9+_-]{1,20}$/
const asText = (b) => `${b.label} = +${b.days} days`

/**
 * PTL Operations → ODA Management → ODA Bands (PTL_ODA_REWORK_DEV_LOG.md): the extra EDD days of the ODA types of the
 * courier zone map rows (ODA 1 / 2 / 3, checked after days set by hand), the km bands for ODA pincodes without an ODA
 * type, and the fallback for ODA pincodes with neither. Draft on screen → Save → confirm with
 * the changes → saved (audited). A band that pincodes still use cannot be removed (the server says how many).
 */
export default function PtlOdaBands() {
  const { enqueueSnackbar } = useSnackbar()
  const [saved, setSaved] = useState(null)
  const [canEdit, setCanEdit] = useState(false)
  const [draft, setDraft] = useState(null)
  const [error, setError] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setError('')
    fetchOdaBands()
      .then((r) => { setSaved(r.bands); setCanEdit(!!r.canEdit); setDraft(copy(r.bands)) })
      .catch((e) => setError(errorText(e, 'Could not load the ODA bands')))
  }, [])
  useEffect(() => { load() }, [load])

  const problems = useMemo(() => (draft ? check(draft) : []), [draft])
  const changes = useMemo(() => (saved && draft ? diff(saved, draft) : []), [saved, draft])
  const dirty = changes.length > 0

  const setCategory = (c, value) => setDraft({ ...draft, categoryDays: { ...draft.categoryDays, [c]: value.replace(/\D/g, '').slice(0, 2) } })
  const setBand = (i, field, value) => setDraft({ ...draft, bands: draft.bands.map((b, j) => (j === i ? { ...b, [field]: value } : b)) })
  const addBand = () => setDraft({ ...draft, bands: [...draft.bands, { key: '', label: '', days: '', isNew: true }] })
  const removeBand = (i) => setDraft({ ...draft, bands: draft.bands.filter((_, j) => j !== i) })

  const save = async () => {
    setBusy(true)
    try {
      const r = await saveOdaBands({
        bands: draft.bands.map((b) => ({ key: b.key.trim(), label: b.label.trim(), days: Number(b.days) })),
        fallbackDays: Number(draft.fallbackDays),
        categoryDays: Object.fromEntries(ODA_TYPE_KEYS.map((c) => [c, Number(draft.categoryDays[c])])),
        updatedAt: saved.updatedAt,
      })
      setConfirmOpen(false)
      setSaved(r.bands)
      setDraft(copy(r.bands))
      enqueueSnackbar('ODA bands saved', { variant: 'success' })
    } catch (e) {
      setConfirmOpen(false)
      enqueueSnackbar(errorText(e, 'Could not save the bands'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const days = (saved?.bands || []).map((b) => b.days)
  const catDays = saved?.categoryDays || {}
  const catsSet = ODA_TYPE_KEYS.every((c) => catDays[c] != null)
  const kpis = [
    { key: 'categories', label: 'ODA 1 / ODA 2 / ODA 3', value: saved ? (catsSet ? ODA_TYPE_KEYS.map((c) => `+${catDays[c]}`).join(' / ') : 'Not set up') : '', sub: 'ODA type days (win over the band)', tone: saved && !catsSet ? 'warn' : undefined },
    { key: 'bands', label: 'Km bands', value: saved ? num(saved.bands.length) : '', sub: 'For ODA pincodes with no ODA type' },
    { key: 'range', label: 'Extra EDD days', value: saved && days.length ? `${Math.min(...days)}–${Math.max(...days)}` : '', sub: 'Shortest to longest band' },
    { key: 'fallback', label: 'Fallback', value: saved ? `+${saved.fallbackDays} days` : '', sub: 'ODA pincodes with no type or band days' },
    { key: 'last', label: 'Last change', value: saved ? fmtDate(saved.updatedAt).split(',')[0] : '', sub: saved?.updatedBy || '' },
  ]

  const cell = { borderColor: C.lineSoft, py: 1 }
  return (
    <PtlPage title='ODA Management' tabTitle='ODA Bands' eyebrow={ODA_EYEBROW} tabs={odaTabs()}
      description={`Extra EDD days of an ODA shipment, from the carrying courier code's zone map row, checked in this order: days set by hand on the pincode; else its ODA type's days (ODA 1 / 2 / 3); else its km band's days; else the fallback. Changes reach the EDD ${EDD_TIMING}; prices never change.`}
      kpis={kpis} kpisLoading={!saved && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {saved && !canEdit && <Banner tone='info'>You can view the bands. Changing them needs PTL_SERVICEABILITY_ADMIN: ask a Proship admin in User Management.</Banner>}

      {draft && (
        <Card elevation={0} sx={{ ...SX.card, p: 2.5 }}>
          <Typography component='h2' sx={TYPE.sectionTitle}>1 · ODA type days</Typography>
          <Typography sx={{ ...TYPE.rowHelp, mt: 0.25, mb: 1.5 }}>
            When the courier code&apos;s zone map marks the drop pincode ODA 1, ODA 2 or ODA 3, the shipment gets these days, unless days are set by hand on the pincode. They win over the km band.
          </Typography>
          {!catsSet && <Banner tone='warn'>ODA type days are not set up yet: until they are, the km band or fallback applies. Enter them below (PTL team: ODA 1 +3, ODA 2 +4, ODA 3 +5).</Banner>}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
            {ODA_TYPE_KEYS.map((c) => (
              <TextField key={c} size='small' label={`${typeLabel(c)} extra days`} value={draft.categoryDays[c]} disabled={!canEdit}
                onChange={(e) => setCategory(c, e.target.value)} inputProps={{ inputMode: 'numeric' }} sx={{ width: 160 }} />
            ))}
          </Box>

          <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 0.25 }}>2 · Km bands (pincodes without an ODA type)</Typography>
          <Typography sx={{ ...TYPE.rowHelp, mb: 1.5 }}>Set per ODA pincode on ODA Pincodes or in the ODA zone upload, from its distance to the city centre or the courier&apos;s hub.</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 560 }} aria-label='ODA km bands'>
              <TableHead>
                <TableRow>
                  {['Band name (shown to ops)', 'Id', 'Extra EDD days', ''].map((h, i) => (
                    <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {draft.bands.map((b, i) => {
                  const isNew = !!b.isNew
                  return (
                    <TableRow key={i} sx={{ '& td': cell }}>
                      <TableCell>
                        <TextField size='small' value={b.label} disabled={!canEdit} onChange={(e) => setBand(i, 'label', e.target.value)}
                          inputProps={{ 'aria-label': `Name of band ${i + 1}`, maxLength: 30 }} sx={{ width: 220 }} />
                      </TableCell>
                      <TableCell>
                        {isNew && canEdit ? (
                          <TextField size='small' value={b.key} placeholder='e.g. 150+' onChange={(e) => setBand(i, 'key', e.target.value)}
                            inputProps={{ 'aria-label': `Id of band ${i + 1}`, maxLength: 20 }} sx={{ width: 140 }} />
                        ) : (
                          <Typography sx={{ fontSize: 12.5, color: C.inkMuted, fontFamily: 'monospace' }} title='The id pincodes refer to; it cannot change'>{b.key}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField size='small' value={b.days} disabled={!canEdit} onChange={(e) => setBand(i, 'days', e.target.value.replace(/\D/g, '').slice(0, 2))}
                          inputProps={{ 'aria-label': `Extra days of band ${i + 1}`, inputMode: 'numeric' }} sx={{ width: 110 }} />
                      </TableCell>
                      <TableCell align='right'>
                        {canEdit && (
                          <IconButton aria-label={`Remove band ${b.label || i + 1}`} onClick={() => removeBand(i)} disabled={draft.bands.length <= 1}>
                            <DeleteOutlineIcon fontSize='small' />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Box>
          {canEdit && <Button startIcon={<AddIcon />} onClick={addBand} sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}>Add band</Button>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 2.5 }}>
            <TextField size='small' label='Fallback extra days' value={draft.fallbackDays} disabled={!canEdit}
              onChange={(e) => setDraft({ ...draft, fallbackDays: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              inputProps={{ inputMode: 'numeric' }} sx={{ width: 180 }} />
            <Typography sx={{ ...TYPE.rowHelp, flex: '1 1 260px' }}>3 · For an ODA pincode with no ODA type days and no km band days. Today’s flat ODA days were 3.</Typography>
          </Box>

          {problems.length > 0 && dirty && <Box sx={{ mt: 2 }}>{problems.map((p) => <Banner key={p} tone='warn'>{p}</Banner>)}</Box>}

          {canEdit && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 2.5, pt: 2, borderTop: `1px solid ${C.line}` }}>
              <Typography role='status' sx={{ ...TYPE.rowHelp, flex: '1 1 240px' }}>{dirty ? `${changes.length} change${changes.length === 1 ? '' : 's'} not saved yet.` : 'No changes.'}</Typography>
              <Button disabled={!dirty || busy} onClick={() => setDraft(copy(saved))} sx={{ textTransform: 'none', fontWeight: 600, color: C.inkMuted }}>Undo changes</Button>
              <Button variant='contained' disabled={!dirty || busy || problems.length > 0} onClick={() => setConfirmOpen(true)} sx={{ ...SX.cta, minHeight: 36 }}>Save bands</Button>
            </Box>
          )}
        </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Save the ODA bands?</DialogTitle>
        <DialogContent>
          {changes.map((c) => <Typography key={c} sx={{ ...TYPE.rowHelp, color: C.inkBody, mb: 0.75 }}>{c}</Typography>)}
          <Typography sx={{ ...TYPE.rowHelp, color: C.inkBody, mt: 1.5 }}>
            Every ODA pincode of a changed ODA type or band, and every pincode on the fallback, gets the new days, {EDD_TIMING}. Pincodes with days set by hand keep theirs.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
          <LoadingButton variant='contained' loading={busy} onClick={save} sx={SX.cta}>Save</LoadingButton>
        </DialogActions>
      </Dialog>
    </PtlPage>
  )
}

const copy = (b) => ({
  bands: b.bands.map((x) => ({ key: x.key, label: x.label, days: String(x.days) })),
  fallbackDays: String(b.fallbackDays),
  categoryDays: Object.fromEntries(ODA_TYPE_KEYS.map((c) => [c, b.categoryDays?.[c] == null ? '' : String(b.categoryDays[c])])),
})

/** What the server would refuse, said before Save. */
function check(d) {
  const out = []
  const keys = new Set()
  const labels = new Set()
  d.bands.forEach((b, i) => {
    const n = b.label.trim() || `Band ${i + 1}`
    if (!b.label.trim()) out.push(`Band ${i + 1} needs a name.`)
    if (!KEY.test(b.key.trim())) out.push(`${n} needs an id of up to 20 letters, digits, + _ or -.`)
    else if (keys.has(b.key.trim())) out.push(`Id ${b.key.trim()} is used twice.`)
    keys.add(b.key.trim())
    if (b.label.trim() && labels.has(b.label.trim().toLowerCase())) out.push(`Name ${b.label.trim()} is used twice.`)
    labels.add(b.label.trim().toLowerCase())
    if (b.days === '' || Number(b.days) > MAX_EXTRA_DAYS) out.push(`${n} needs extra days from 0 to ${MAX_EXTRA_DAYS}.`)
  })
  if (d.fallbackDays === '' || Number(d.fallbackDays) > MAX_EXTRA_DAYS) out.push(`Fallback needs extra days from 0 to ${MAX_EXTRA_DAYS}.`)
  ODA_TYPE_KEYS.forEach((c) => {
    if (d.categoryDays[c] === '' || Number(d.categoryDays[c]) > MAX_EXTRA_DAYS) out.push(`${typeLabel(c)} needs extra days from 0 to ${MAX_EXTRA_DAYS}.`)
  })
  return out
}

/** The draft against the saved bands, one line per change, for the confirm dialog. */
function diff(saved, d) {
  const out = []
  const before = new Map(saved.bands.map((b) => [b.key, b]))
  const after = new Map(d.bands.map((b) => [b.key.trim(), { ...b, days: Number(b.days) }]))
  after.forEach((b, k) => {
    const o = before.get(k)
    if (!o) out.push(`New band: ${asText(b)}`)
    else if (o.label !== b.label.trim() || o.days !== b.days) out.push(`${o.label}: ${o.label !== b.label.trim() ? `renamed ${b.label.trim()}, ` : ''}+${o.days} → +${b.days} days`)
  })
  before.forEach((o, k) => { if (!after.has(k)) out.push(`Removed: ${asText(o)}`) })
  if (String(saved.fallbackDays) !== String(Number(d.fallbackDays))) out.push(`Fallback: +${saved.fallbackDays} → +${Number(d.fallbackDays)} days`)
  ODA_TYPE_KEYS.forEach((c) => {
    const was = saved.categoryDays?.[c]
    if (d.categoryDays[c] !== '' && String(was) !== String(Number(d.categoryDays[c]))) out.push(`${typeLabel(c)}: ${was == null ? 'not set' : `+${was}`} → +${Number(d.categoryDays[c])} days`)
  })
  return out
}
